import React, { useEffect, useRef, useState } from 'react'
import {
  CAlert, CButton, CCol, CFormInput, CFormLabel, CFormSelect, CRow,
  CModal, CModalHeader, CModalTitle, CModalBody,
} from '@coreui/react'
import { api } from '../api/client'
import { hoyLocal } from '../utils/fecha'
import { capitalizarPalabras } from '../utils/texto'
import { normalizarNumeroTexto } from '../utils/numeros'
import { fmtMoneda } from '../utils/moneda'

/**
 * SubirPagoModal — "Subir pago de Suscripción" COMPARTIDO (CuentaPage y
 * PagoPage): el cliente pagó por otro medio (transferencia/giro) y registra su
 * pago con comprobante OBLIGATORIO; queda PENDIENTE de confirmación del
 * superadmin (PagoConfirmado = 2, grabarpago.cs:114 — el backend lo fuerza
 * para el autoservicio). Réplica del panel GX subirpagosistema.cs:1106
 * (combos Pago Tipo/Entidad y labels dinámicos por entidad) [RGM].
 */
/** Datos del módulo de recibo por RUTA que se paga con este comprobante. */
export interface ModuloRutaPago { tipo: string; zonaCodigo: number }

interface SubirPagoModalProps {
  visible: boolean
  onClose: () => void
  /** Próximo cobro de la empresa (se envía en el body como el panel GX). */
  proximoCobro?: string
  /** [RGM] 2026-09-29 (B4): plan actual — para el tipo 7 (Actualización de
   *  Plan) solo se ofrecen planes mayores; prellena la diferencia. */
  planActual?: number
  /** Tipo preseleccionado del combo Pago Tipo al abrir (ej. 5 = Módulo
   *  Facturación desde su tarjeta de Mi Cuenta). Sin prop: vacío. */
  tipoInicial?: number | ''
  /** Módulo de recibo por RUTA (Tickets de impresión / WhatsApp): fija
   *  PagoTipo 3, la ruta en PagoLibreNumerico1 y el tipo de activación en
   *  PagoLibreCaracter2 — al confirmar el superadmin se enciende el módulo de
   *  ESA ruta (PagoService.ModificarPagoAsync). [RGM] 2026-09-28. */
  moduloRuta?: ModuloRutaPago
  /** Se invoca al subir el pago con éxito (los padres refrescan sus datos). */
  onSubido?: () => void
}

const SubirPagoModal: React.FC<SubirPagoModalProps> = ({
  visible, onClose, proximoCobro, tipoInicial, moduloRuta, onSubido, planActual,
}) => {
  const [tipo, setTipo] = useState('')
  // [RGM] 2026-09-29 (B4): plan nuevo de la actualización (tipo 7).
  const [planNuevo, setPlanNuevo] = useState('')
  // [RGM] 2026-09-29 (pedido Rafael: "debe exigirse elegir plan al pagar el
  // registro o subir el pago"): plan del REGISTRO (tipo 1) — obligatorio.
  const [planRegistro, setPlanRegistro] = useState('')
  const [valor, setValor] = useState('')
  const [fechaPago, setFechaPago] = useState('')
  const [entidad, setEntidad] = useState('')
  const [referencia, setReferencia] = useState('')
  const [remitente, setRemitente] = useState('')
  const [ciudad, setCiudad] = useState('')
  const [pais, setPais] = useState('')
  const [subiendoSubirPago, setSubiendoSubirPago] = useState(false)
  // Comprobante de pago (imagen): se sube a R2 y se guarda la clave corta que
  // viaja en pagoLibreCaracter1 (char 30). [RGM] 2026-09-16 (pedido Rafael).
  const [comprobanteKey, setComprobanteKey] = useState('')
  const [comprobantePreview, setComprobantePreview] = useState('')
  const [subiendoComprobante, setSubiendoComprobante] = useState(false)
  const [error, setError] = useState('')
  const [mensaje, setMensaje] = useState('')
  // Ref del formulario para forzar los requeridos GX (submit type="button").
  // [RGM] 2026-09-15.
  const subirPagoRef = useRef<HTMLDivElement>(null)

  // Preselección del tipo al ABRIR el modal (el estado interno persiste entre
  // aperturas: se sincroniza cuando `visible` pasa a true). Sin prop, vacío.
  // [RGM] 2026-09-28 / 2026-09-29: con moduloRuta el tipo se fija según el
  // módulo de la ruta — 6 = solo Tickets de impresión (activar-impresora),
  // 3 = Impresora - WhatsApp (activar-whatsapp y combo).
  useEffect(() => {
    if (!visible) return
    if (moduloRuta) setTipo(moduloRuta.tipo === 'activar-impresora' ? '6' : '3')
    else setTipo(tipoInicial !== undefined && tipoInicial !== '' ? String(tipoInicial) : '')
  }, [visible, tipoInicial, moduloRuta])

  // Limpia el formulario (al cerrar o tras subir el pago). [RGM]
  const limpiar = () => {
    setTipo('')
    setPlanNuevo('')
    setPlanRegistro('')
    setValor('')
    setFechaPago('')
    setEntidad('')
    setReferencia('')
    setRemitente('')
    setCiudad('')
    setPais('')
    setComprobanteKey('')
    setComprobantePreview('')
  }

  const cerrar = () => {
    // [RGM] 2026-09-28 (pedido Rafael): si se subió un comprobante y el pago
    // NO se envía (cierra/cancela), se elimina de R2 para no dejar huérfanos.
    if (comprobanteKey) {
      api.delete(`/pagos/comprobante/${encodeURIComponent(comprobanteKey)}`).catch(() => undefined)
    }
    limpiar()
    setError('')
    setMensaje('')
    onClose()
  }

  // Subir Pago — replica del flujo GX: el panel subirpagosistema llama a
  // grabarpago (grabarpago.cs:110-128), que inserta el pago con la empresa del
  // token, PagoFecha = HOY (linea 113) y PagoConfirmado = 2 (PENDIENTE,
  // linea 114) — NO toca el ciclo de cobro (se envia el ProximoCobro actual).
  // [RGM] 2026-09-29 (pedido Rafael): el autoservicio ya NO exige cubrir el
  // total (la validación GX pago_bc.cs:379-381 quedó solo para el alta manual
  // del superadmin) — el comprobante puede subirse por un valor parcial:
  // queda pendiente de confirmación.
  const subirPagoSistema = async () => {
    // [RGM] 2026-09-28: guard anti doble-clic (además del disabled del botón)
    // para no disparar el POST varias veces.
    if (subiendoSubirPago) return
    // [RGM] 2026-09-15 — requeridos GX efectivos (subirpagosistema.cs:885-925):
    // valida los 8 campos con la validación nativa del navegador antes del POST.
    const invalido = subirPagoRef.current?.querySelector<HTMLInputElement>(':invalid')
    if (invalido) { invalido.reportValidity(); return }
    // [RGM] 2026-09-16 (pedido Rafael): el comprobante es OBLIGATORIO en el
    // autoservicio (banco/giro) — sin imagen no se sube el pago.
    if (subiendoComprobante) { setError('Espera a que termine de subir el comprobante.'); return }
    if (!comprobanteKey) { setError('Sube el comprobante de pago (imagen o PDF).'); return }
    // [RGM] 2026-11-04 (caso real PRESTA FÁCIL VD: "One or more validation
    // errors occurred." en inglés): el POST enviaba pagoFechaPago: "" cuando la
    // fecha no estaba elegida y el binding de DateTime reventaba con 400
    // genérico. Ahora se exige con mensaje claro y los campos fecha viajan
    // SOLO si son ISO válidos.
    if (!fechaPago) { setError('Selecciona la Fecha Pago (la fecha del comprobante).'); return }
    setError('')
    setMensaje('')
    setSubiendoSubirPago(true)
    try {
      // [RGM] 2026-10-09 (pedido Rafael — caso real MAIN #19/#20: la misma
      // referencia bancaria subida 2 veces con 2½ min de diferencia): aviso
      // anti-duplicado ANTES del POST. Se consulta el listado de pagos de la
      // empresa (GET /pagos — la empresa la resuelve el token vía
      // ResolverEmpresa; el query vacío es requerido por el binding) y si ya
      // hay un comprobante PENDIENTE (PagoConfirmado = 2) con la MISMA
      // referencia (comparación trim; si la referencia viene vacía, mismo
      // valor + fecha como tentativo) se pide confirmación bloqueante. Si
      // cancela, NO se crea el pago.
      // [RGM] 2026-11-04 (bug real PRESTA FÁCIL VD / "One or more validation
      // errors occurred."): se llamaba con `?empresa=` VACÍO y el binding
      // Required del parámetro no-nullable devolvía 400 en inglés. La empresa
      // la resuelve el token en el backend; el query va vacío/omitido.
      const pagosEmpresa = await api.get<any[]>('/pagos')
      const referenciaNormalizada = referencia.trim()
      const yaPendiente = pagosEmpresa.some((p) => Number(p.pagoConfirmado) === 2 && (
        referenciaNormalizada !== ''
          ? String(p.pagoRefrencia ?? '').trim() === referenciaNormalizada
          : Number(p.pagoValor ?? 0) === Number(valor)
            && String(p.pagoFechaPago ?? '').slice(0, 10) === String(fechaPago)
      ))
      if (yaPendiente && !window.confirm('Ya subiste un comprobante con esta referencia. ¿Subir otro igual?')) {
        return
      }
      await api.post('/pagos', {
        valor: Number(valor),
        // GX grabarpago.cs:113 — PagoFecha = HOY (no la fecha del comprobante).
        fecha: hoyLocal(),
        // La empresa sale del token; el backend también fija el próximo cobro
        // real para el autoservicio. [RGM] 2026-11-04: solo viaja si es una
        // fecha ISO válida (un "" rompía el binding con el 400 genérico).
        proximoCobro: /^\d{4}-\d{2}-\d{2}/.test(proximoCobro ?? '') ? proximoCobro : undefined,
        pagoConfirmado: 2, // GX grabarpago.cs:114 — queda PENDIENTE
        // [RGM] 2026-11-04: nunca enviar "" (DateTime? no acepta string vacío);
        // la fecha se exige arriba con mensaje claro.
        pagoFechaPago: fechaPago || undefined,
        // [RGM] 2026-09-28 / 2026-09-29: módulo de recibo por RUTA → tipo 6
        // (solo tickets) o 3 (Impresora - WhatsApp); la ruta y el tipo de
        // activación viajan en los libres para que al confirmar el superadmin
        // se encienda el módulo de ESA ruta.
        pagoTipo: moduloRuta ? (moduloRuta.tipo === 'activar-impresora' ? 6 : 3) : Number(tipo),
        // [RGM] 2026-09-29 (B4): plan nuevo de la Actualización de Plan (tipo 7).
        pagoLibreNumerico2: Number(tipo) === 7 ? Number(planNuevo) : undefined,
        // [RGM] 2026-09-29 (pedido Rafael): plan del REGISTRO (tipo 1) — al
        // confirmar el superadmin la empresa queda con ese plan.
        pagoLibreNumerico3: Number(tipo) === 1 ? Number(planRegistro) : undefined,
        entidad: Number(entidad) || 0,
        pagoRefrencia: referencia,
        remitente: remitente || undefined,
        pagoCiudad: ciudad || undefined,
        pagoPais: pais || undefined,
        pagoLibreNumerico1: moduloRuta?.zonaCodigo,
        pagoLibreCaracter2: moduloRuta?.tipo,
        // Comprobante: clave corta en R2 (PagoLibreCaracter1). [RGM] 2026-09-16.
        pagoLibreCaracter1: comprobanteKey || undefined,
      })
      // Éxito: mensaje EXACTO de siempre; el form queda limpio y el padre
      // refresca sus datos (la X cierra el modal).
      setMensaje('Pago subido — queda pendiente de confirmación.')
      limpiar()
      onSubido?.()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSubiendoSubirPago(false)
    }
  }

  // Sube la imagen del comprobante a R2 (clave corta) — obligatoria en Subir Pago.
  // [RGM] 2026-09-16 (pedido Rafael: banco/giro suben la imagen del comprobante).
  const onComprobanteChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const anterior = comprobanteKey
    setError('')
    setSubiendoComprobante(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post<{ key: string; url: string }>('/pagos/comprobante', fd)
      setComprobanteKey(res.key)
      setComprobantePreview(res.url)
      // [RGM] 2026-09-28 (pedido Rafael): si se reemplaza un comprobante ya
      // subido, se borra el anterior de R2.
      if (anterior) {
        api.delete(`/pagos/comprobante/${encodeURIComponent(anterior)}`).catch(() => undefined)
      }
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo subir el comprobante.')
    } finally {
      setSubiendoComprobante(false)
    }
  }

  // [RGM] 2026-09-15 — Etiquetas dinámicas del panel GX (Validv_Pagoentidad,
  // subirpagosistema.cs:1114-1146): al elegir la Entidad, el caption del campo
  // Referencia (y del Remitente para Wompi 13) cambia en el GX.
  const etiquetaRemitente = entidad === '13' ? 'E-mail de Pago' : 'Nombre Remitente'
  const etiquetaReferencia = entidad === '1' ? 'Clave de Envio MTCN'
    : entidad === '2' ? 'Número de Referencia'
      : entidad === '7' ? 'PIN'
        : entidad === '8' ? 'Factura de Venta'
          : 'Referencia de Pago'

  return (
    <CModal visible={visible} onClose={cerrar} size="lg">
      <CModalHeader>
        <CModalTitle>Subir pago de Suscripción</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {error && <CAlert color="danger" className="py-2">{error}</CAlert>}
        {mensaje && <CAlert color="success" className="py-2">{mensaje}</CAlert>}
        <div ref={subirPagoRef} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }}>
          <CRow className="g-3">
            <CCol md={6}>
              <CFormLabel>Pago Tipo</CFormLabel>
              <CFormSelect required value={tipo} disabled={!!moduloRuta}
                onChange={(e) => setTipo(e.target.value)}>
                <option value=""></option>
                <option value={2}>Mantenimiento</option>
                <option value={1}>Registro</option>
                <option value={3}>Impresora - WhatsApp</option>
                <option value={4}>Asesoría Personalizada</option>
                {/* [RGM] 2026-09-29 (pedido Rafael): tipo 6 = solo Tickets de
                    impresión — el módulo solo-ticket lo fija al pagar por ruta. */}
                <option value={6}>Tickets de Impresión</option>
                {/* [RGM] 2026-09-29 (B4): actualización a un plan mayor — se
                    paga la DIFERENCIA; el plan nuevo se elige al lado. */}
                <option value={7}>Actualización de Plan</option>
                {/* [RGM] Módulo Facturación (PagoTipo 5): al confirmar el
                    superadmin se activa EmpresaModuloFacturacion. */}
                <option value={5}>Módulo Facturación</option>
              </CFormSelect>
              {/* [RGM] 2026-09-28: el comprobante de un módulo de recibo por
                  ruta solo enciende la ruta cuando el superadmin lo confirma. */}
              {moduloRuta && (
                <small className="text-body-secondary d-block mt-1">
                  El módulo se activa cuando el administrador confirme el pago.
                </small>
              )}
            </CCol>
            {tipo === '1' && (
              <CCol md={6}>
                <CFormLabel>Plan (obligatorio)</CFormLabel>
                <CFormSelect required value={planRegistro}
                  onChange={(e) => {
                    setPlanRegistro(e.target.value)
                    // Prellena el valor con el precio PROMO del plan (50% OFF).
                    const promo: Record<number, number> = { 1: 200000, 2: 400000, 3: 600000 }
                    const n = Number(e.target.value)
                    if (n > 0) setValor(String(promo[n]))
                  }}>
                  <option value=""></option>
                  <option value={1}>Unipersonal — {fmtMoneda(200000)}</option>
                  <option value={2}>Duo — {fmtMoneda(400000)}</option>
                  <option value={3}>Empresa — {fmtMoneda(600000)}</option>
                </CFormSelect>
              </CCol>
            )}
            {Number(tipo) === 7 && (
              <CCol md={6}>
                <CFormLabel>Plan nuevo</CFormLabel>
                <CFormSelect required value={planNuevo}
                  onChange={(e) => {
                    setPlanNuevo(e.target.value)
                    // Prellena la DIFERENCIA (promo 50%: U $200.000 · D
                    // $400.000 · E $600.000 — misma cuenta que valida el server).
                    const promo: Record<number, number> = { 1: 200000, 2: 400000, 3: 600000 }
                    const n = Number(e.target.value)
                    const actual = Number(planActual ?? 0)
                    if (n > 0) setValor(String(promo[n] - promo[actual]))
                  }}>
                  <option value=""></option>
                  {[2, 3].filter((p) => p > Number(planActual ?? 0)).map((p) => {
                    const promo: Record<number, number> = { 1: 200000, 2: 400000, 3: 600000 }
                    const dif = promo[p] - promo[Number(planActual ?? 0)]
                    return (
                      <option key={p} value={p}>
                        {p === 2 ? 'Duo' : 'Empresa'} — diferencia {fmtMoneda(dif)}
                      </option>
                    )
                  })}
                </CFormSelect>
              </CCol>
            )}
            <CCol md={6}>
              <CFormLabel>Valor del Pago</CFormLabel>
              <CFormInput type="number" step="0.01" min="0" required
                value={valor} onChange={(e) => setValor(e.target.value)} onBlur={(e) => setValor(normalizarNumeroTexto(e.target.value))} />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Fecha Pago</CFormLabel>
              <CFormInput type="date" required
                value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Entidad</CFormLabel>
              <CFormSelect required value={entidad} onChange={(e) => setEntidad(e.target.value)}>
                {/* Item 0 del combo GX = placeholder: el required
                    GX exige entidad != 0. */}
                <option value="">Dónde Realizó el Pago?</option>
                <option value={1}>Western Union</option>
                <option value={2}>Money Gram</option>
                <option value={3}>Vigo</option>
                <option value={4}>Afex</option>
                <option value={5}>Bancolombia</option>
                <option value={6}>Giros y Finanzas</option>
                <option value={7}>Gane - Super Giros</option>
                <option value={8}>Efecty</option>
                <option value={9}>Cybertronica</option>
                <option value={10}>PayU</option>
                <option value={11}>Paypal</option>
                <option value={12}>Online</option>
                <option value={13}>Wompi</option>
              </CFormSelect>
            </CCol>
            <CCol md={6}>
              <CFormLabel>{etiquetaReferencia}</CFormLabel>
              <CFormInput required maxLength={60}
                value={referencia} onChange={(e) => setReferencia(e.target.value)} />
            </CCol>
            <CCol md={6}>
              <CFormLabel>{etiquetaRemitente}</CFormLabel>
              {/* Wompi (entidad 13) usa este campo como "E-mail de Pago":
                  los emails NO se capitalizan (excepción de la regla E). */}
              <CFormInput required maxLength={30}
                value={remitente} onChange={(e) => setRemitente(e.target.value)} onBlur={(e) => setRemitente(entidad === '13' ? e.target.value : capitalizarPalabras(e.target.value))} />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Ciudad</CFormLabel>
              <CFormInput required maxLength={30}
                value={ciudad} onChange={(e) => setCiudad(e.target.value)} onBlur={(e) => setCiudad(capitalizarPalabras(e.target.value))} />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Pais</CFormLabel>
              <CFormInput required maxLength={30}
                value={pais} onChange={(e) => setPais(e.target.value)} onBlur={(e) => setPais(capitalizarPalabras(e.target.value))} />
            </CCol>
            {/* Comprobante (imagen, OBLIGATORIO): banco/giro pagan
                con comprobante; se sube a R2 y queda el link en el
                historial. [RGM] 2026-09-16 (pedido Rafael). */}
            <CCol md={12}>
              <CFormLabel>Comprobante (imagen o PDF) — requerido</CFormLabel>
              <CFormInput type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.pdf"
                onChange={onComprobanteChange} disabled={subiendoComprobante} />
              {/* [RGM] 2026-09-28 (pedido Rafael): indicar formatos aceptados y peso máximo. */}
              <small className="text-body-secondary d-block mt-1">
                Formatos aceptados: JPG, PNG, WebP o PDF · Tamaño máximo: 10 MB.
              </small>
              {subiendoComprobante && (
                <small className="text-body-secondary">Subiendo comprobante...</small>
              )}
              {comprobantePreview && !subiendoComprobante && (
                <small>
                  <a href={comprobantePreview} target="_blank" rel="noreferrer">
                    Ver comprobante subido
                  </a>
                </small>
              )}
            </CCol>
          </CRow>
          {/* Nota del propio panel GX (subirpagosistema.cs:532). */}
          <div className="text-body-secondary mt-2" style={{ fontSize: 12 }}>
            Envíanos el Comprobante por Whatsapp +573168750841
          </div>
          {/* [RGM] 2026-09-29 (pedido Rafael: "después de los pagos el botón
              subir pago debe esconderse y quedar un botón Volver o salir"):
              tras subir con éxito se oculta "Subir Pago" y queda "Volver". */}
          <div className="d-flex justify-content-end mt-3">
            {mensaje ? (
              <CButton color="secondary" type="button" onClick={cerrar}>
                Volver
              </CButton>
            ) : (
              <CButton color="primary" type="button" disabled={subiendoSubirPago}
                onClick={subirPagoSistema}>
                {subiendoSubirPago ? 'Subiendo...' : 'Subir Pago'}
              </CButton>
            )}
          </div>
        </div>
      </CModalBody>
    </CModal>
  )
}

export default SubirPagoModal
